import api from './client'
import { reportTaskStatus } from './taskTicks'

// Уникальный ключ идемпотентности на каждый клик «Сгенерировать»/«Пересоздать».
// Новый UUID на клик → одинаковый промпт+референс даёт НОВУЮ генерацию;
// сетевой ретрай того же клика несёт тот же ключ → без повторного списания.
function genIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fallthrough */
  }
  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

export interface GenerateImageRequest {
  prompt: string
  provider: string
  model?: string
  negative_prompt?: string
  width?: number
  height?: number
  aspect_ratio?: string
  resolution?: string
  steps?: number
  guidance?: number
  safety_filter_level?: string
  output_format?: string
  style?: string
  image_input?: string[]
  google_search?: boolean
  quality?: string
  num_outputs?: number
  /** Карточка «Нарисовать» в чате (backend services/chat_image_card): чат и сообщение с предложением — готовая
   *  картинка придёт в этот чат сообщением ассистента. */
  chat_session_id?: string
  chat_message_id?: string
  /** Какие входные картинки карточки брать: source и/или reference; «Нарисовать заново» — ['reference']. */
  chat_use_inputs?: Array<'source' | 'reference'>
  /** Цена на кнопке карточки в момент нажатия: текущая выше — сервер ответит 409 price_changed с новой. */
  chat_shown_price?: number
}

export interface GenerateResponse {
  ok: boolean
  image_url?: string
  video_url?: string
  request_id?: string
  task_id?: string
  credits_spent?: number
  status?: string
  error?: string
  error_ru?: string
}

export interface TaskStatusResponse {
  request_id: string
  status: string
  type: string
  model: string
  result_url?: string
  result_urls?: string[]
  /** Для audio-тасков Suno — URL обложек параллельно с result_urls (index-aligned).
   *  null для не-audio. Бэк JOIN'ит suno_tracks.image_url. */
  image_urls?: string[]
  credits_spent?: number
  error_code?: string
  error_message?: string
  error_message_ru?: string
  user_message?: string
  image_errors?: string[]
  /** Для операций которые возвращают структурированные данные вместо файла
   *  (Suno MIDI, timestamped-lyrics и др.). Shape зависит от типа операции. */
  response_data?: unknown
  /** Звук видео с треком (28.09): провайдер не пропустил звук готового ролика — сервер пересоздаёт его,
   *  генерация идёт дальше (списано один раз). attempt — номер идущей попытки. */
  retrying?: boolean
  attempt?: number | null
  /** У видео был трек (карточки или свой) — доступна «Наложить трек». */
  has_track?: boolean
  /** Звук уже наш слой ('our_layer') или это версия по кнопке ('button'); null — звук модели. */
  sound_source?: string | null
  /** Готовая версия с треком по кнопке. */
  track_version?: TrackVersion | null
}

export interface TrackVersion {
  request_id: string
  result_url: string
}

export interface OverlayTrackResponse extends TrackVersion {
  ok: boolean
  source_request_id: string
}

export interface NanoBananaRequest {
  prompt: string
  model?: string
  aspect_ratio?: string
  resolution?: string
  output_format?: string
  image_input?: string[]
}

export interface MidjourneyRequest {
  prompt: string
  task_type?: string
  file_url?: string
  aspect_ratio?: string
  version?: string
  speed?: string
  stylization?: number
  weirdness?: number
}

export interface ImageToImageRequest {
  prompt: string
  provider: string
  model?: string
  image_url: string
  aspect_ratio?: string
  version?: string
  speed?: string
  stylization?: number
}

export interface VideoGenerateRequest {
  prompt: string
  /** Цена на кнопке: при плате за секунды входного видео сервер не спишет больше (input_video_billing). */
  expected_credits?: number
  provider?: string
  model?: string
  image_urls?: string[]
  video_urls?: string[]
  end_image_url?: string
  reference_image_urls?: string[]
  duration?: string
  aspect_ratio?: string
  sound?: boolean
  character_orientation?: string
  keep_original_sound?: boolean
  resolution?: string
  prompt_optimizer?: boolean
  generate_audio?: boolean
  negative_prompt?: string
  video_reference_type?: string
  multi_prompt?: string
  multi_shots?: boolean
  kling_elements?: Array<Record<string, unknown>>
  first_frame_url?: string
  last_frame_url?: string
  /** Черновик Seedance 2.5: проба в 480p, из которой потом делается финал 1080p (backend: services/seedance_draft). */
  draft?: boolean
}

/** Ответ GET /api/v1/video/draft-info: доступен ли черновик и цены черновика и финала для ролика такой длины. */
export interface DraftInfoResponse {
  ok: boolean
  available: boolean
  duration?: number
  /** Сколько суток провайдер хранит черновик — столько можно делать из него финал (с запасом на сервере). */
  ttl_days?: number
  draft?: { resolution: string; credits: number }
  final?: { resolution: string; credits: number }
}

export interface MidjourneyVideoRequest {
  prompt: string
  image_url: string
  model?: string
}

export async function generateImage(request: GenerateImageRequest): Promise<GenerateResponse> {
  return api.request<GenerateResponse>('/api/v1/images/generate', {
    method: 'POST',
    headers: { 'X-Idempotency-Key': genIdempotencyKey() },
    body: request,
  })
}

export async function generateImageAsync(request: GenerateImageRequest): Promise<GenerateResponse> {
  return api.request<GenerateResponse>('/api/v1/images/generate-async', {
    method: 'POST',
    headers: { 'X-Idempotency-Key': genIdempotencyKey() },
    body: request,
  })
}

export async function getTaskStatus(requestId: string): Promise<TaskStatusResponse> {
  const s = await api.request<TaskStatusResponse>(`/api/v1/tasks/${requestId}`, {
    method: 'GET',
  })
  // Каждый ответ — всей странице: «пересоздаём ролик» видно там, где генерацию рисуют (api/taskTicks).
  reportTaskStatus(s)
  return s
}

/** «Наложить трек» (28.09): версия ролика с треком генерации — бесплатно, модель не запускается. Путь вне шаблона
 *  generate: клиент API не подмешивает сюда поля генерации (edit_source_request_id и др.). */
export async function overlayTrack(requestId: string): Promise<OverlayTrackResponse> {
  return api.request<OverlayTrackResponse>('/api/v1/video/overlay-track', {
    method: 'POST',
    body: { request_id: requestId },
  })
}

/** Генерации нет и не будет (сервер не знает её у этого пользователя: 404, чужая: 403, негодный номер: 422). Опрос
 *  без потолка (план «Ждём ответа нейросети», Ф2) на таком ответе останавливается, остальные сбои — повторяются. */
export function isTaskGone(e: unknown): boolean {
  const status = (e as { status?: number } | null)?.status
  return status === 404 || status === 403 || status === 422
}

export const TASK_GONE_MESSAGE = 'Генерация не найдена — возможно, вы вошли под другим аккаунтом'

export async function generateNanoBanana(request: NanoBananaRequest): Promise<GenerateResponse> {
  return api.request<GenerateResponse>('/api/v1/images/nano-banana', {
    method: 'POST',
    headers: { 'X-Idempotency-Key': genIdempotencyKey() },
    body: request,
  })
}

export async function generateMidjourney(request: MidjourneyRequest): Promise<GenerateResponse> {
  return api.request<GenerateResponse>('/api/v1/images/midjourney', {
    method: 'POST',
    headers: { 'X-Idempotency-Key': genIdempotencyKey() },
    body: request,
  })
}

export async function imageToImage(request: ImageToImageRequest): Promise<GenerateResponse> {
  return api.request<GenerateResponse>('/api/v1/images/image-to-image', {
    method: 'POST',
    headers: { 'X-Idempotency-Key': genIdempotencyKey() },
    body: request,
  })
}

/** Длина своих видео на входе — замер сервера, тем же способом, что у списания платы за секунды входного видео
 *  (backend POST /video/input-meta). Чужие ссылки сервер не меряет — у них seconds: null. */
export async function videoInputMeta(urls: string[]): Promise<{ url: string; seconds: number | null }[]> {
  const res = await api.request<{ ok: boolean; items?: { url: string; seconds: number | null }[] }>('/api/v1/video/input-meta', {
    method: 'POST',
    body: { urls },
  })
  return res?.items ?? []
}

export async function generateVideo(request: VideoGenerateRequest): Promise<GenerateResponse> {
  return api.request<GenerateResponse>('/api/v1/video/generate-async', {
    method: 'POST',
    headers: { 'X-Idempotency-Key': genIdempotencyKey() },
    body: request,
  })
}

export interface MotionRiskWarning {
  code: 'short_upper_body' | 'many_people' | string
  text: string
}

export interface MotionPrecheckResponse {
  warning: MotionRiskWarning | null
  measured: boolean
  frames?: number
  seconds?: number
}

/** Kling Motion Control: откажет ли Kling по ролику (замер позы на сервере, калибровка 12.10). Меряется та часть ролика,
 *  что уйдёт в модель (длительность и ориентация — как у запуска). Подсказка, а не проверка: сбой — warning пустой. */
export async function motionPrecheck(
  body: { video_url: string; duration: number; orientation?: string },
  signal?: AbortSignal,
): Promise<MotionPrecheckResponse> {
  return api.request<MotionPrecheckResponse>('/api/v1/video/motion-precheck', { method: 'POST', body, signal })
}

/** Человек из ролика режима «Повторить движение» (services/motion_people): описание для промпта, подпись для
 *  интерфейса, где стоит, когда появляется, плитка-кадр. */
export interface MotionPerson {
  id: number
  /** По-английски: пол, возраст, сложение, волосы, одежда с цветами — так нейросеть находит человека в ролике. */
  desc: string
  /** По-русски, коротко: «мужчина в сером свитере». */
  label: string
  position?: 'left' | 'center' | 'right' | null
  /** Секунда первого появления. */
  first_t?: number | null
  /** Кадр с этим человеком; нет — в плитке номер. */
  crop_url?: string | null
}

export interface MotionPeopleResponse {
  people: MotionPerson[]
  measured: boolean
}

/** Кто в ролике: сервер смотрит четыре кадра и описывает людей (14.10). Только ролик из нашего хранилища; сбой или ролик
 *  без людей — пустой список: режим остаётся со слотами по порядку. */
export async function motionPeople(body: { video_url: string }, signal?: AbortSignal): Promise<MotionPeopleResponse> {
  return api.request<MotionPeopleResponse>('/api/v1/video/motion-people', { method: 'POST', body, signal })
}

/** Доступен ли черновик Seedance 2.5 и цены: черновик 480p и финал 1080p для ролика такой длины. Недоступно (строку
 *  «Адаптеров» выключили, нет ключа) — `available: false`, переключатель «Черновик» прячется. */
export async function getDraftInfo(duration: number, sound: boolean): Promise<DraftInfoResponse> {
  return api.request<DraftInfoResponse>(
    `/api/v1/video/draft-info?duration=${encodeURIComponent(String(duration))}&sound=${sound ? 'true' : 'false'}`,
    { method: 'GET' },
  )
}

/** Финал 1080p из готового черновика. Тело пустое: промпт, референсы, длительность и звук провайдер берёт у черновика.
 *  Путь вне шаблона generate: клиент API не подмешивает сюда поля генерации (папку и цепочку студии сервер берёт у
 *  самого черновика). 409 — финал из этого черновика уже сделан или делается, 402 — не хватает токенов. */
export async function finalizeVideoDraft(requestId: string): Promise<GenerateResponse> {
  return api.request<GenerateResponse>(`/api/v1/video/${encodeURIComponent(requestId)}/finalize`, {
    method: 'POST',
    headers: { 'X-Idempotency-Key': genIdempotencyKey() },
    body: {},
  })
}

export async function generateMidjourneyVideo(request: MidjourneyVideoRequest): Promise<GenerateResponse> {
  return api.request<GenerateResponse>('/api/v1/video/midjourney', {
    method: 'POST',
    headers: { 'X-Idempotency-Key': genIdempotencyKey() },
    body: request,
  })
}

export interface FileUploadResponse {
  id: string
  key: string
  filename: string
  original_filename?: string
  category: string
  content_type?: string
  size_bytes: number
  url: string
  /** Обложка для audio-файлов сгенерированных Suno. JOIN suno_tracks.image_url
   *  через source_request_id. null для image/video/uploads/остальных audio. */
  cover_url?: string | null
  /** URL WAV-версии трека (если юзер конвертировал). Null если ещё не было
   *  конвертации. Используется чтобы показать 2-ю кнопку download для audio. */
  wav_url?: string | null
  /** UUID Suno-трека — нужен для lookup WAV в клиентском кэше (fallback
   *  пока backend не начнёт отдавать wav_url напрямую). */
  suno_track_id?: string | null
}

export async function uploadFile(file: File, category: string = 'images'): Promise<FileUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const token = api.getToken()
  const API_URL = import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8100'
    : '')

  const response = await fetch(`${API_URL}/api/v1/files/upload?category=${category}`, {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: formData,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }))
    throw new Error(error.detail || 'Upload failed')
  }

  return response.json()
}

export async function getFileUrl(fileId: string): Promise<{ url: string }> {
  return api.request<{ url: string }>(`/api/v1/files/url/${fileId}`, {
    method: 'GET',
  })
}

export interface SaveFromUrlRequest {
  url: string
  category?: string
  filename_hint?: string
}

export async function saveFromUrl(request: SaveFromUrlRequest): Promise<FileUploadResponse> {
  return api.request<FileUploadResponse>('/api/v1/files/save-from-url', {
    method: 'POST',
    body: request,
  })
}

export async function downloadVideoFromUrl(url: string, maxDuration = 15): Promise<FileUploadResponse> {
  return api.request<FileUploadResponse>('/api/v1/files/download-video', {
    method: 'POST',
    body: { url, max_duration: maxDuration },
  })
}

export async function trimVideoOnServer(url: string, start: number, end: number): Promise<FileUploadResponse> {
  return api.request<FileUploadResponse>('/api/v1/files/trim-video', {
    method: 'POST',
    body: { url, start, end },
  })
}

export async function rateGeneration(requestId: string, rating: number): Promise<void> {
  await api.request('/api/v1/images/rate', {
    method: 'POST',
    body: { request_id: requestId, rating },
  })
}

/** Название работы («кабинета») студии — пишется в params корневой
 *  генерации цепочки, панель кабинетов показывает его вместо промпта. */
/** «Удаление» работы из студии — мягкое скрытие: файлы остаются в «Моих
 *  генерациях», hidden=false возвращает работу в столы (решение владельца). */
export async function setStudioVisibility(
  requestIds: string[],
  hidden: boolean,
): Promise<{ restored?: string[]; folders?: number }> {
  // Возврат (hidden=false) отвечает, какие генерации снова видны: файл, спрятанный вместе с работой, возвращает
  // её целиком (Ф4 папок) — «Мои генерации» снимают пометку со всех её файлов.
  return api.request('/api/v1/images/studio-visibility', {
    method: 'POST',
    body: { request_ids: requestIds, hidden },
  })
}

export async function setWorkTitle(requestId: string, title: string): Promise<void> {
  await api.request('/api/v1/images/work-title', {
    method: 'POST',
    body: { request_id: requestId, title },
  })
}